// Lightweight wrapper around the `opus-decoder` WASM package for use in the browser.
//
// Uses https://www.npmjs.com/package/opus-decoder, which inlines WASM and exposes a
// promise-based `ready` property plus `decodeFrame()` for Opus frames.
//
// This wrapper presents a simple interface expected by RealtimeAPI.opusDecoder:
//   async decode(frameBuffer: ArrayBuffer, sampleRate: number) => Int16Array

import { OpusDecoder } from 'opus-decoder';

export class BrowserOpusDecoder {
  /**
   * @param {number} sampleRate - Target PCM sample rate for decoded audio (e.g. 24000)
   * @param {number} channels - Number of channels (1 for mono)
   * @param {boolean} debug - Enable debug logging
   */
  constructor(sampleRate = 24000, channels = 1, debug = false) {
    this.sampleRate = sampleRate;
    this.channels = channels;
    this.debug = debug;

    // Create decoder; WASM compilation is async and exposed via .ready
    this.decoder = new OpusDecoder({
      outputSampleRate: this.sampleRate,
      channels: this.channels,
    });
    this.ready = this.decoder.ready;
  }

  /**
   * Decode Opus payload (may contain multiple concatenated packets) into PCM16 samples.
   * This matches the interface expected by RealtimeAPI.opusDecoder:
   *   decode(payload: ArrayBuffer, sampleRate: number, expectedSampleCount?: number) => Promise<Int16Array>
   *
   * @param {ArrayBuffer} frameBuffer - Raw Opus payload bytes (may be concatenated packets)
   * @param {number} sampleRate - Sample rate from the header (decoded PCM rate)
   * @param {number} [expectedSampleCount] - Expected sample count from backend header (optional)
   * @returns {Promise<Int16Array>}
   */
  async decode(frameBuffer, sampleRate, expectedSampleCount) {
    const targetRate = Number(sampleRate) || this.sampleRate;

    // If backend changes the sample rate mid-stream, reset the decoder to match.
    if (targetRate !== this.sampleRate) {
      this.sampleRate = targetRate;
      if (this.decoder && typeof this.decoder.free === 'function') {
        this.decoder.free();
      }
      this.decoder = new OpusDecoder({
        outputSampleRate: this.sampleRate,
        channels: this.channels,
      });
      this.ready = this.decoder.ready;
    }

    // Ensure WASM is compiled
    if (this.ready) {
      await this.ready;
    }

    const payload = new Uint8Array(frameBuffer);
    
    // CRITICAL FIX: Backend sends FRAMED Opus packets with 2-byte length prefixes
    // Format: [2 bytes: len1][packet1][2 bytes: len2][packet2]...
    // We need to parse and decode ALL packets
    
    // Step 1: Parse framed packets
    const packets = this._parseFramedPackets(payload);
    
    if (packets.length === 0) {
      console.error('[OpusDecoder] No valid packets found in payload');
      // Return silence
      return new Int16Array(480);
    }
    
    // Step 2: Decode all packets
    const allSamples = [];
    let failedPackets = 0;
    
    for (let i = 0; i < packets.length; i++) {
      try {
        const packet = packets[i];
        
        // Decode one packet
        const result = this.decoder.decodeFrame(packet);
        
        if (!result || !result.channelData || !result.channelData[0]) {
          failedPackets++;
          // Insert silence for failed packet (assume 480 samples)
          allSamples.push(...new Float32Array(480));
          continue;
        }
        
        const pcmFloat = result.channelData[0];
        const samplesDecoded = result.samplesDecoded || pcmFloat.length;
        
        if (samplesDecoded === 0) {
          failedPackets++;
          continue;
        }
        
        // Add decoded samples to our collection
        allSamples.push(...pcmFloat);
        
      } catch (error) {
        failedPackets++;
        // Insert silence for failed packet
        allSamples.push(...new Float32Array(480));
      }
    }
    
    if (failedPackets > 0) {
      console.warn(`[OpusDecoder] ${failedPackets}/${packets.length} packets failed to decode`);
    }
    
    if (this.debug) {
      console.log(`[OpusDecoder] ✅ Decoded ${packets.length} packets, ${allSamples.length} total samples from ${payload.length} bytes`);
      
      // Check for silence or low audio levels (may indicate issues)
      if (allSamples.length > 0) {
        const maxAmplitude = Math.max(...allSamples.map(Math.abs));
        const rms = Math.sqrt(allSamples.reduce((sum, s) => sum + s * s, 0) / allSamples.length);
        console.log(`[OpusDecoder] Audio levels - Max: ${maxAmplitude.toFixed(4)}, RMS: ${rms.toFixed(4)}`);
        
        if (maxAmplitude < 0.001) {
          console.warn(`[OpusDecoder] ⚠️ Very low audio level detected - may be silence or corruption`);
        }
      }
    }
    
    // Combine all decoded samples
    let pcmFloat = new Float32Array(allSamples);
    
    // Remove DC offset to prevent clicks at silence transitions
    if (pcmFloat.length > 0) {
      const dcOffset = pcmFloat.reduce((sum, s) => sum + s, 0) / pcmFloat.length;
      if (Math.abs(dcOffset) > 0.001) {
        for (let i = 0; i < pcmFloat.length; i++) {
          pcmFloat[i] -= dcOffset;
        }
      }
    }
    
    // CRITICAL FIX: opus-decoder may decode at 48kHz even when we request 24kHz
    // Detect actual decoded rate from sample count
    // Each packet should be 20ms, so we can calculate: samples_per_packet / 0.02 = sample_rate
    const samplesPerPacket = packets.length > 0 ? allSamples.length / packets.length : 0;
    const detectedRate = samplesPerPacket > 0 ? Math.round(samplesPerPacket / 0.02) : this.sampleRate;
    
    // We need to downsample if the actual decoded rate doesn't match target
    let finalPcmFloat = pcmFloat;
    const actualRate = detectedRate;
    
    // OPTIMIZATION: Skip resampling if target is 48kHz and decoder outputs 48kHz
    // This avoids resampling artifacts and preserves native Opus quality
    if (actualRate && Math.abs(actualRate - targetRate) > 100) {
      // Resample needed (e.g., 48kHz -> 24kHz)
      const ratio = targetRate / actualRate;
      const newLength = Math.floor(pcmFloat.length * ratio);
      const resampled = new Float32Array(newLength);
      
      for (let i = 0; i < newLength; i++) {
        const srcPos = i / ratio;
        const srcIdx = Math.floor(srcPos);
        const frac = srcPos - srcIdx;
        const s0 = pcmFloat[srcIdx] || 0;
        const s1 = pcmFloat[srcIdx + 1] || s0;
        resampled[i] = s0 + (s1 - s0) * frac;
      }
      
      finalPcmFloat = resampled;
    }
    
    // CRITICAL FIX: If expectedSampleCount is provided, adjust output to match exactly
    // This prevents timing discontinuities when backend expects specific sample counts
    if (expectedSampleCount && expectedSampleCount > 0) {
      const currentLength = finalPcmFloat.length;
      const diff = Math.abs(currentLength - expectedSampleCount);
      
      // Only adjust if difference is VERY small (< 5% tolerance)
      // Larger differences indicate backend metadata errors - trust the decoder instead
      const tolerance = expectedSampleCount * 0.05;
      
      if (diff > 0 && diff <= tolerance) {
        if (currentLength > expectedSampleCount) {
          // Trim excess samples
          finalPcmFloat = finalPcmFloat.slice(0, expectedSampleCount);
        } else if (currentLength < expectedSampleCount) {
          // Pad with zeros (silence)
          const padded = new Float32Array(expectedSampleCount);
          padded.set(finalPcmFloat);
          finalPcmFloat = padded;
        }
      }
    }
    
    // Convert Float32 to Int16
    const len = finalPcmFloat.length;
    const pcmInt16 = new Int16Array(len);
    for (let i = 0; i < len; i++) {
      let s = finalPcmFloat[i] * 32767;
      if (s > 32767) s = 32767;
      else if (s < -32768) s = -32768;
      pcmInt16[i] = s | 0;
    }

    return pcmInt16;
  }

  /**
   * Parse framed Opus packets with 2-byte length prefixes.
   * Backend sends format: [2 bytes: len1][packet1][2 bytes: len2][packet2]...
   * Length is big-endian (network byte order).
   * 
   * @param {Uint8Array} payload - Framed Opus payload
   * @returns {Array<Uint8Array>} Array of individual Opus packets
   * @private
   */
  _parseFramedPackets(payload) {
    const packets = [];
    let offset = 0;
    
    while (offset < payload.length) {
      // Check if we have at least 2 bytes for length prefix
      if (offset + 2 > payload.length) {
        break;
      }
      
      // Read 2-byte packet length (big-endian / network byte order)
      const packetLength = (payload[offset] << 8) | payload[offset + 1];
      offset += 2;
      
      // Validate packet length
      if (packetLength === 0) {
        continue;
      }
      
      if (packetLength > 2000) {
        console.error(
          `[OpusDecoder] Suspiciously large packet: ${packetLength} bytes at offset ${offset - 2}. Possible corruption.`
        );
        break;
      }
      
      // Check if we have enough data for the packet
      if (offset + packetLength > payload.length) {
        console.error(
          `[OpusDecoder] Incomplete packet: expected ${packetLength} bytes, ` +
          `only ${payload.length - offset} available at offset ${offset}`
        );
        break;
      }
      
      // Extract packet data
      const packet = payload.slice(offset, offset + packetLength);
      packets.push(packet);
      offset += packetLength;
    }
    
    return packets;
  }
}

export default BrowserOpusDecoder;
